import { useCallback, useRef } from 'react';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { isDeafConnectedSelector } from 'features/call/call-deaf/store';
import type { DialPadValue } from 'features/dial-pad/interfaces';
import {
  sendDtmfThunkOutbound,
  sendDtmfThunkInbound,
  appendDialedNumberToState,
  isDialPadOpenedSelector,
} from 'features/dial-pad/store';
import { voiceSessionIdSelector } from 'features/voice-session/store';
import { isPrimaryVcoSenderSelector } from 'features/vco/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { LogLevel } from 'features/analytics/enums';
import { useTaskQueue } from 'features/dial-pad/hooks';

interface UseDtmfHandlerProps {
  participantId: string | null;
  onSoundAdd: (value: DialPadValue) => void;
}

export const useDtmfHandler = ({
  participantId,
  onSoundAdd,
}: UseDtmfHandlerProps) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);
  const isDialPadOpened = useAppSelector(isDialPadOpenedSelector);
  const isDeafConnected = useAppSelector(isDeafConnectedSelector);
  const isPrimaryVcoSender = useAppSelector(isPrimaryVcoSenderSelector);

  // Custom hooks
  const { addTask } = useTaskQueue();

  // Refs
  const isProcessingDtmfRef = useRef(false);
  const lastOutboundDtmfRef = useRef<{
    value: string;
    timestamp: number;
  } | null>(null);

  const sendDtmfToHearing = useCallback(
    (value: DialPadValue) => {
      // Prevent overlapping DTMF processing
      if (isProcessingDtmfRef.current || !isPrimaryVcoSender) {
        return;
      }

      isProcessingDtmfRef.current = true;

      try {
        // Track outbound DTMF to prevent echo processing
        lastOutboundDtmfRef.current = {
          value: value.toString(),
          timestamp: Date.now(),
        };

        onSoundAdd(value);
        dispatch(appendDialedNumberToState(value.toString()));

        const dtmfPayload = {
          value,
          participantId: participantId ?? undefined,
          sessionId: voiceSessionId,
          isDeafConnected,
        };

        if (participantId) {
          addTask(async () => {
            await dispatch(sendDtmfThunkOutbound(dtmfPayload)).unwrap();
          });
        } else {
          dispatch(sendDtmfThunkOutbound(dtmfPayload));
        }
      } finally {
        isProcessingDtmfRef.current = false;
      }
    },
    [
      isPrimaryVcoSender,
      dispatch,
      participantId,
      voiceSessionId,
      isDeafConnected,
      addTask,
      onSoundAdd,
    ]
  );

  const handleIncomingDtmf = useCallback(
    (incomingRequest: { body: string }) => {
      if (!participantId || !isDialPadOpened || !isPrimaryVcoSender) {
        return;
      }

      const signalMatch = /Signal=(\d+)/.exec(incomingRequest.body);
      const digit = signalMatch?.[1] ?? '';

      let normalizedDigit: DialPadValue;
      switch (digit) {
        case '10':
          normalizedDigit = '*';
          break;
        case '11':
          normalizedDigit = '#';
          break;
        default:
          normalizedDigit = digit as DialPadValue;
      }

      // Detect and log feedback from our own outbound sends
      const now = Date.now();
      const DTMF_FEEDBACK_LOOP_MS = 2000;
      if (
        lastOutboundDtmfRef.current?.value === normalizedDigit &&
        now - lastOutboundDtmfRef.current.timestamp < DTMF_FEEDBACK_LOOP_MS
      ) {
        // Dispatch analytics to track feedback occurrences in production
        dispatch(
          sendAnalyticsInfo({
            Method: 'handleIncomingDtmf',
            Level: LogLevel.INFO,
            Message: `DTMF Feedback Loop Detected. (${now - lastOutboundDtmfRef.current.timestamp}ms delay), duplicate digit will be ignored`,
          })
        );

        // Prevent processing the feedback
        return;
      }

      onSoundAdd(normalizedDigit);

      addTask(async () => {
        await dispatch(
          sendDtmfThunkInbound({
            value: normalizedDigit,
            participantId,
            sessionId: voiceSessionId,
          })
        ).unwrap();
      });
    },
    [
      participantId,
      isDialPadOpened,
      isPrimaryVcoSender,
      addTask,
      dispatch,
      voiceSessionId,
      onSoundAdd,
    ]
  );

  return {
    sendDtmfToHearing,
    handleIncomingDtmf,
  };
};
